import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import ChapterRow, { ChapterCta, ManuscriptNote, ChapterBridge } from "./ChapterRow";

/**
 * Chapter II — THE LAND
 * Question: Where does the tobacco come from? Why does geography matter?
 *
 * Visual centerpiece: archival 19th-century engraving of a Dominican tobacco
 * plantation — mountains, tobacco rows, curing houses. Used 1:1 (no filters,
 * no vectorization, no stylization) per editorial directive.
 */

const LAND_PLATE =
  "https://customer-assets.emergentagent.com/job_heritage-blend-4/artifacts/beemsak0_magnific_ultra-detailed-19thcentur_2956176189.png";

export default function ChapterLand() {
  const { t, lang } = useI18n();

  const epigraphEN = (
    <>
      <span className="text-tobacco/90">Where does Ramírez tobacco come from?</span>
      <br />
      From the disciplined south — Las Matas de Farfán, Elías Piña.
    </>
  );
  const epigraphES = (
    <>
      <span className="text-tobacco/90">¿De dónde viene el tabaco Ramírez?</span>
      <br />
      Del sur disciplinado — Las Matas de Farfán, Elías Piña.
    </>
  );

  return (
    <ChapterRow
      id="land"
      testid="chapter-land"
      numeral="II"
      name="THE LAND"
      chapterLabel="Chapter II — The Land"
      epigraph={lang === "es" ? epigraphES : epigraphEN}
      headline={t("land.title")}
      body={
        <>
          <p className="text-tobacco/85 text-[15.5px] md:text-[15px] leading-[1.65] font-medium">
            {t("land.body_1")}
          </p>
          <p className="text-tobacco/65 italic font-display text-[16px] leading-snug pt-2">
            {lang === "es"
              ? "“La tierra del sur enseña paciencia.”"
              : "“The southern earth teaches patience.”"}
          </p>
        </>
      }
      cta={
        <ChapterCta
          testid="land-cta"
          onClick={() => document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" })}
        >
          {lang === "es" ? "Continuar al Oficio" : "Continue to The Craft"} →
        </ChapterCta>
      }
      illustration={
        <figure className="w-full" data-testid="land-plate-figure">
          <div className="w-full overflow-hidden">
            <img
              src={LAND_PLATE}
              alt={
                lang === "es"
                  ? "Plantación de tabaco dominicana — Las Matas de Farfán, Elías Piña, vista hacia la Sierra de Neiba con casas de curado"
                  : "Dominican tobacco plantation — Las Matas de Farfán, Elías Piña, looking toward the Sierra de Neiba with curing houses"
              }
              data-testid="land-plate"
              loading="lazy"
              decoding="async"
              className="w-full h-auto block select-none pointer-events-none"
              style={{
                // Mobile: subtle bias toward keeping mountains + curing houses
                // visible. Desktop: render the engraving in its full
                // editorial composition without crop.
                maxHeight: "clamp(360px, 56vw, 620px)",
                objectFit: "cover",
                objectPosition: "center 55%",
              }}
            />
          </div>
          <figcaption className="mt-4 flex items-baseline justify-between font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55">
            <span>Plate · III</span>
            <span className="hidden md:inline italic font-display text-[13px] text-tobacco/70 normal-case tracking-normal">
              {lang === "es"
                ? "Las Matas de Farfán — campo de tabaco al pie de la Sierra de Neiba"
                : "Las Matas de Farfán — tobacco field at the foot of the Sierra de Neiba"}
            </span>
            <span>Folio · 003</span>
          </figcaption>
        </figure>
      }
      meta={
        <ManuscriptNote
          label={lang === "es" ? "Geografía" : "Geography"}
          items={[
            { k: lang === "es" ? "Región" : "Region", v: "San Juan · Elías Piña" },
            { k: lang === "es" ? "Elevación" : "Elevation", v: "420 – 760 m" },
            {
              k: lang === "es" ? "Suelo" : "Soil",
              v: lang === "es" ? "Franco arcilloso" : "Clay-loam",
            },
            { k: lang === "es" ? "Cosecha" : "Harvest", v: "Feb – Apr" },
          ]}
        />
      }
      bridge={
        <ChapterBridge
          left={
            lang === "es"
              ? "Lo que la tierra ofrece, las manos deben merecerlo."
              : "What the land offers, the hands must earn."
          }
          right={lang === "es" ? "Capítulo III · El Oficio" : "Chapter III · The Craft"}
          testid="bridge-ii-to-iii"
          onClick={() => document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" })}
        />
      }
      bg="bg-pearl-warm"
    />
  );
}
